import {
  deleteUser, faq, logout, updateUserProfile, cancelSubscription, deviceTokens,
} from 'common/utils/network/endpoints';
import { Capacitor } from '@capacitor/core';
import { baseApi } from 'common/store/baseApi';
import { getStoredPushToken, clearStoredPushToken } from 'common/utils/pushRegistration';
import { clearSpotlight } from 'common/utils/spotlight';
import { clearWidgetSnapshot } from 'common/utils/widgetSnapshot';
import { wipeAll as wipeOfflineVault } from 'common/utils/offlineVault';
import {
  FAQResponse, LogoutResponse, UpdateUserProfileResponse,
  ChangeEmailRequestArgs, ChangeEmailRequestResponse, ConfirmEmailChangeArgs, ConfirmEmailChangeResponse,
} from './types';

// Verified change of login address. Kept local rather than in
// network/endpoints so this slice owns the whole flow.
const changeEmailRequest = '/user/email/change-request';
const confirmEmailChange = '/user/email/confirm';
// Legacy Stripe (Cashier) monthly -> annual. GET: eligibility + price, POST: switch.
const switchToAnnual = '/user/subscription/switch-to-annual';

export type AnnualSwitchPreview = {
  eligible: boolean;
  already_annual: boolean;
  on_trial: boolean;
  current_period_end: string | null;
  annual_price: { amount: number; currency: string; formatted: string } | null;
};

export type SwitchToAnnualResult = {
  already_annual: boolean;
  current_period_end: string | null;
};

export const MenuBarServiceAPI = baseApi
  .enhanceEndpoints({ addTagTypes: [] })
  .injectEndpoints({
    endpoints: (builder) => ({
      logoutAction: builder.mutation<LogoutResponse, void>({
        // queryFn instead of query so the device push token can be
        // unregistered BEFORE logout revokes the auth token — the DELETE
        // needs the session to still be valid. Best-effort: a failed
        // unregister never blocks logout.
        queryFn: async (_arg, _api, _extraOptions, fetchWithBQ) => {
          if (Capacitor.isNativePlatform()) {
            try {
              const pushToken = await getStoredPushToken();
              if (pushToken) {
                // Short timeout: a hung unregister must never block logout.
                const res = await fetchWithBQ({
                  url: deviceTokens,
                  method: 'DELETE',
                  body: { token: pushToken },
                  timeout: 5000,
                });
                // Keep the local record when the DELETE failed so the next
                // login's re-registration overwrites the same backend row.
                if (!res.error) {
                  await clearStoredPushToken();
                }
              }
            } catch (error) {
              console.warn('Push unregister on logout failed (non-fatal):', error);
            }
          }

          const result = await fetchWithBQ({ url: logout, method: 'POST' });
          return result.error
            ? { error: result.error }
            : { data: result.data as LogoutResponse };
        },
      }),
      updateUserProfileAction: builder.mutation<UpdateUserProfileResponse, any>({
        query: (arg) => ({
          url: updateUserProfile.replace('%userId%', arg.userId.toString()),
          method: 'POST',
          body: arg.formData,
        }),
      }),
      // Step one: new address + current password. The server parks the
      // address and emails a six-digit code to it; nothing changes yet.
      changeEmailRequestAction: builder.mutation<ChangeEmailRequestResponse, ChangeEmailRequestArgs>({
        query: (body) => ({
          url: changeEmailRequest,
          method: 'POST',
          body,
        }),
      }),
      // Step two: the code from the new inbox. Returns the updated profile.
      confirmEmailChangeAction: builder.mutation<ConfirmEmailChangeResponse, ConfirmEmailChangeArgs>({
        query: (body) => ({
          url: confirmEmailChange,
          method: 'POST',
          body,
        }),
      }),
      faqAction: builder.mutation<FAQResponse, void>({
        query: () => ({
          url: faq,
          method: 'GET',
        }),
      }),
      // Account deletion leaves no account to log back into and no future
      // logout to trigger clearCredentials/onLogoutHandler's own Spotlight
      // purge, so it has to happen here instead - covered by construction
      // (hooked on the mutation, not left to the handler to remember)
      // rather than relying on onDeleteUserHandler to also call it. Without
      // this, every document name the deleted account ever had would stay
      // permanently searchable on the device with no way left to switch it
      // off.
      deleteUserAction: builder.mutation<any, void>({
        query: () => ({
          url: deleteUser,
          method: 'DELETE',
        }),
        async onQueryStarted(_arg, { queryFulfilled }) {
          try {
            await queryFulfilled;
            await clearSpotlight();
            // Same reasoning as clearSpotlight above: no future logout
            // will fire to trigger clearCredentials/onLogoutHandler's own
            // widget-snapshot purge, so a deleted account's renewals would
            // otherwise stay on the home-screen widget indefinitely.
            await clearWidgetSnapshot();
            // Same reasoning again, and the most consequential of the three:
            // a deleted account whose documents stayed encrypted-but-present
            // on the device would outlive the account itself.
            await wipeOfflineVault();
          } catch {
            // Account deletion failed server-side - leave the index alone.
          }
        },
      }),
      annualSwitchPreview: builder.query<AnnualSwitchPreview, void>({
        query: () => ({ url: switchToAnnual, method: 'GET' }),
        transformResponse: (res: { data: AnnualSwitchPreview }) => res.data,
      }),
      switchToAnnualAction: builder.mutation<SwitchToAnnualResult, void>({
        query: () => ({ url: switchToAnnual, method: 'POST' }),
        transformResponse: (res: { data: SwitchToAnnualResult }) => res.data,
      }),
      cancelSubscriptionAction: builder.mutation<any, void>({
        query: () => ({
          url: cancelSubscription,
          method: 'POST',
        }),
      }),
    }),
  });

export const {
  useLogoutActionMutation,
  useUpdateUserProfileActionMutation,
  useChangeEmailRequestActionMutation,
  useConfirmEmailChangeActionMutation,
  useFaqActionMutation,
  useDeleteUserActionMutation,
  useCancelSubscriptionActionMutation,
  useAnnualSwitchPreviewQuery,
  useSwitchToAnnualActionMutation,
} = MenuBarServiceAPI;

export default MenuBarServiceAPI;
